import React, { useState } from "react";
import { ArtistLocationsSearchRequest } from "models/ArtistLocationsSearchRequest";
import { ApiService } from "services/ApiService";
import { ArtistDto } from "models/ArtistDto";
import { useNavigate } from "react-router-dom";

const ArtistLocationSearch: React.FC = () => {
    const [form, setForm] = useState<ArtistLocationsSearchRequest>(new ArtistLocationsSearchRequest());
    const [results, setResults] = useState<ArtistDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState(""); // For dropdown search
    const [selectedArtist, setSelectedArtist] = useState<ArtistDto | null>(null);
    const navigate = useNavigate();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "number" ? (value === "" ? undefined : Number(value)) : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await ApiService.getArtistLocationsBySearchRequest(form);
            console.log(response);
            if (response.success && response.data) {
                setResults(response.data);
            } else {
                setResults([]);
                alert("No results found.");
            }
        } catch (error) {
            setResults([]);
            alert("Error searching for artist locations.");
        }
        setLoading(false);
    };

    const filteredResults = results.filter(
        (artist) =>
            artist.firstName.toLowerCase().includes(search.toLowerCase()) ||
            artist.lastName.toLowerCase().includes(search.toLowerCase()) ||
            artist.userName.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelectedArtist = (artist: ArtistDto) => {
        setSelectedArtist(artist);
        navigate(`/artist/${artist.id}`);
    }

    return (
        <div className="max-w-xl mx-auto p-4">
            <h2 className="text-2xl font-bold mb-4">Search for Tattoo Artists Near You!</h2>
            <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Column 1: City/State */}
                    <div>
                        <label className="block font-medium">City</label>
                        <input
                            type="text"
                            name="city"
                            value={form.city || ""}
                            onChange={handleChange}
                            className="border rounded px-2 py-1 w-full"
                        />
                        <label className="block font-medium mt-2">State</label>
                        <input
                            type="text"
                            name="state"
                            value={form.state || ""}
                            onChange={handleChange}
                            className="border rounded px-2 py-1 w-full"
                        />
                    </div>
                    {/* Column 2: Postal Code */}
                    <div>
                        <label className="block font-medium">Postal Code</label>
                        <input
                            type="text"
                            name="postalCode"
                            value={form.postalCode || ""}
                            onChange={handleChange}
                            className="border rounded px-2 py-1 w-full"
                        />
                    </div>
                    {/* Column 3: Lat/Lng + Button */}
                    <div>
                        <div className="flex items-end gap-2">
                            <div className="flex-1">
                                <label className="block font-medium">Latitude</label>
                                <input
                                    type="number"
                                    name="latitude"
                                    value={form.latitude ?? ""}
                                    onChange={handleChange}
                                    className="border rounded px-2 py-1 w-full"
                                    step="any"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block font-medium">Longitude</label>
                                <input
                                    type="number"
                                    name="longitude"
                                    value={form.longitude ?? ""}
                                    onChange={handleChange}
                                    className="border rounded px-2 py-1 w-full"
                                    step="any"
                                />
                            </div>
                        </div>
                        <button
                            type="button"
                            className="bg-gray-500 text-white px-3 py-1 rounded mt-2 w-full"
                            onClick={() => {
                                navigator.geolocation.getCurrentPosition(
                                    (position) => {
                                        const { latitude, longitude } = position.coords;
                                        setForm((prev) => ({
                                            ...prev,
                                            latitude,
                                            longitude,
                                        }));
                                    },
                                    () => alert("Unable to retrieve your location.")
                                );
                            }}
                        >
                            Use My Location
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                        <label className="block font-medium">Miles Radius</label>
                        <input
                            type="number"
                            name="milesRadius"
                            value={form.milesRadius ?? ""}
                            onChange={handleChange}
                            className="border rounded px-2 py-1 w-full"
                            step="any"
                        />
                    </div>
                    <div>
                        <label className="block font-medium">Start Date</label>
                        <input
                            type="date"
                            name="startDate"
                            value={form.startDate || ""}
                            onChange={handleChange}
                            className="border rounded px-2 py-1 w-full"
                        />
                    </div>
                    <div>
                        <label className="block font-medium">End Date</label>
                        <input
                            type="date"
                            name="endDate"
                            value={form.endDate || ""}
                            onChange={handleChange}
                            className="border rounded px-2 py-1 w-full"
                        />
                    </div>
                </div>
                <button
                    type="submit"
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mt-4"
                    disabled={loading}
                >
                    {loading ? "Searching..." : "Search"}
                </button>
            </form>

            <div className="mt-6">
                {results.length > 0 && (
                    <div>
                        <label className="block font-medium mb-1">Search Artists</label>
                        <input
                            type="text"
                            placeholder="Type to search artist..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="border rounded px-2 py-1 w-full mb-2"
                        />
                        <ul className="border rounded max-h-60 overflow-y-auto bg-white">
                            {filteredResults.length > 0 ? (
                                filteredResults.map((artist) => (
                                    <li
                                        key={artist.id}
                                        className={`px-4 py-2 cursor-pointer hover:bg-blue-100 ${selectedArtist?.id === artist.id ? "bg-blue-200" : ""
                                            }`}
                                        onClick={() => handleSelectedArtist(artist)}
                                    >
                                        <div className="font-semibold">{artist.firstName} {artist.lastName}</div>
                                        <div className="text-sm text-gray-600">{artist.userName}</div>
                                    </li>
                                ))
                            ) : (
                                <li className="px-4 py-2 text-gray-500">No artists found.</li>
                            )}
                        </ul>
                        {selectedArtist && (
                            <div className="mt-4 p-2 border rounded bg-gray-50">
                                <div><strong>Name:</strong> {selectedArtist.firstName} {selectedArtist.lastName}</div>
                                <div><strong>Username:</strong> {selectedArtist.userName}</div>
                            </div>
                        )}
                    </div>
                )}
                {results.length === 0 && !loading && <div>No results found.</div>}
            </div>
        </div>
    );
};

export default ArtistLocationSearch;